'use client';

import { useState } from 'react';
import { useAlertMutations } from '@/hooks/use-alerts';
import { FeedAlertDialog } from '@/new_components/alerts/FeedAlertDialog';
import { SubscribeAfterSaveDialog } from '@/new_components/alerts/SubscribeAfterSaveDialog';

export interface SavedSearchRef {
  id: number;
  name: string;
}

/** The feed a saved search subscribes to; filters live on the saved search. */
const SAVED_SEARCH_FEED = 'saved_search';
const SAVED_SEARCH_FILTERS: Record<string, unknown> = {};

/**
 * After a search is saved (free): "subscribe to new matches?" with the
 * price. Subscribing creates the same `saved_search` feed alert the saved
 * searches page offers, daily by default; "Anpassen" opens that page's
 * FeedAlertDialog for cadence, channel and recipients.
 */
export function SavedSearchSubscribePrompt({
  search,
  onClose,
}: {
  /** The search just saved; `null` keeps the prompt closed. */
  search: SavedSearchRef | null;
  onClose: () => void;
}) {
  const { create } = useAlertMutations();
  const [customizing, setCustomizing] = useState<SavedSearchRef | null>(null);

  return (
    <>
      <SubscribeAfterSaveDialog
        open={search != null}
        onOpenChange={(open) => !open && onClose()}
        kind="search"
        quoteInput={
          search
            ? {
                subject_type: 'feed',
                feed_type: SAVED_SEARCH_FEED,
                cadence: 'daily',
                filters: SAVED_SEARCH_FILTERS,
                saved_search_id: search.id,
              }
            : null
        }
        onSubscribe={async () => {
          if (!search) return;
          await create.mutateAsync({
            feed_type: SAVED_SEARCH_FEED,
            filters: SAVED_SEARCH_FILTERS,
            cadence: 'daily',
            channel: 'email',
            name: search.name.slice(0, 120),
            saved_search_id: search.id,
          });
        }}
        onCustomize={() => setCustomizing(search)}
      />

      <FeedAlertDialog
        open={customizing != null}
        onOpenChange={(open) => !open && setCustomizing(null)}
        feedType={SAVED_SEARCH_FEED}
        filters={SAVED_SEARCH_FILTERS}
        savedSearchId={customizing?.id}
        defaultName={customizing?.name}
      />
    </>
  );
}
